import type { Dict } from '@/types/dict'
import { zip } from '@/utils/array'
import { unique } from 'math'
import { ref, type Ref } from 'vue'
import type { FunctionalComponent, MaybeRef } from 'vue'
import type { FormFieldDefinition } from './field'
import type { FormEventHooks } from './eventHook'
import type { FormInstance } from './instance'

export type FormLayoutDefinition<State extends Dict, ComputedData extends Dict, ExternalData extends Dict = Dict> = {
  name?: string;
  title?: string;
  description?: string;
  collapsible?: boolean;
  replace?: boolean
  key?: string
  render?: string;
  hidden?: MaybeRef<boolean>;
  needs?: string[];
  hooks?: FormEventHooks<State, string>;
  memo?: (state: State) => Array<any>
  component?: (state: State, form: FormInstance<State, ComputedData, ExternalData>) => FunctionalComponent<{ state: Ref<State>, data: Ref<ComputedData> }>
  override?: Partial<FormFieldDefinition<State>>
  attributes?: Partial<FormFieldDefinition<State>>
  children?:
    | Array<FormLayoutDefinition<State, ComputedData, ExternalData> | ((state: State, form: FormInstance<State, ComputedData, ExternalData>) => FormLayoutDefinition<State, ComputedData, ExternalData>) | undefined | null | false>
    | ((state: State, form: FormInstance<State, ComputedData, ExternalData>) => Array<FormLayoutDefinition<State, ComputedData, ExternalData> | undefined | null | false>)
}

export type FormLayoutNode<State extends Dict, ComputedData extends Dict, ExternalData extends Dict = Dict> = Omit<
  FormLayoutDefinition<State, ComputedData>,
  'children' | 'component'
> & {
  children: Ref<FormLayoutNode<State, ComputedData, ExternalData>[]>;
  deps: string[];
  component: Ref<FunctionalComponent<{ state: Ref<State>, data: Ref<ComputedData> }> | null | undefined>;
  componentConstructor?: (state: State, form: FormInstance<State, ComputedData, ExternalData>) => FunctionalComponent<{ state: Ref<State>, data: Ref<ComputedData> }>;
  version: Ref<number>;
  memoKey?: string
  __v_skip?: true
  //  '__v_skip': true;
}

export const getNextLayoutNode = <State extends Dict, ComputedData extends Dict = Dict, ExternalData extends Dict = Dict>(
  node: FormLayoutDefinition<State, ComputedData, ExternalData>,
  state: State,
  cache: Map<string, FormLayoutNode<State, ComputedData, ExternalData>>,
  form: FormInstance<State, ComputedData, ExternalData>
): FormLayoutNode<State, ComputedData, ExternalData> => {
  if (!node.key) {
    //autoKeyLayoutDefinition(node, state)
  }

  if (node.key && node.memo) {
    const old = cache.get(node.key)
    if (old) {
      const nextMemo = JSON.stringify(node.memo(state))
      if (nextMemo === old.memoKey && old.key === node.key) {
        return old
      } else {
        cache.delete(node.key)
      }
    }
  }
  const old = node.key ? cache.get(node.key) : null



  const nextChilds = (typeof node.children === 'function' ? node.children(state, form) : node.children || [])
    .filter((it) => typeof it !== 'undefined' && it !== null && it !== false)
    .map(it => typeof it === 'function' ? it(state, form) : it)
    .map((child) => getNextLayoutNode(child, state, cache, form))

  //if (old && old.children && old.children.value) {
  //  const oldChilds = old?.children?.value || [];

  //  for (const oldChild of oldChilds) {
  //    if (oldChild.key) {
  //      const nextChild = nextChilds.find(it => it.key === oldChild.key);
  //      if (!nextChild) {
  //        console.log(`Deleted: ${oldChild.key}`);
  //        old.children.value = old.children.value.filter(it => it.key !== oldChild.key);
  //      }
  //    }
  //  }
  //}

  const component = old?.component?.value || (node.component ? node.component(state, form) : undefined)

  const next: FormLayoutNode<State, ComputedData, ExternalData> = {
    ...node,
    children: ref(nextChilds) as unknown as any as Ref<FormLayoutNode<State, ComputedData, ExternalData>[]>,
    component: ref(component),
    memoKey: node.memo ? JSON.stringify(node.memo(state)) : undefined,
    componentConstructor: node.component,
    version: ref<number>(0),
    deps: [],
    __v_skip: true
  }

  if (node.key && !cache.has(node.key)) {
    cache.set(node.key, next)
  }

  return next
}

export const patchArray = (old: any[], next: any[]) => {
  for (let i = 0; i < old.length; i++) {
    if (!next[i]) {
      old.splice(i, 1)
    }
  }

  const z = zip(old, next)
  for (const [a, b] of z) {
    const keys = unique([...Object.keys(a), ...Object.keys(b)])
    for (const key of keys) {
      a[key] = b[key]
    }
  }

  next.slice(z.length).forEach((child) => {
    old.push(child)
  })
}

export const patchLayout = <State extends Dict, ComputedData extends Dict = Dict, ExternalData extends Dict = Dict>(
  old: FormLayoutNode<State, ComputedData, ExternalData>,
  next: FormLayoutNode<State, ComputedData, ExternalData>
): FormLayoutNode<State, ComputedData, ExternalData> => {
  if (old === next) return old;

  for (let i = 0; i < old.children.value.length; i++) {
    if (!next.children.value[i]) {
      old.children.value.splice(i--, 1)
    }
  }

  if (old && old.children && old.children.value) {
    const oldChilds = old?.children?.value || [];

    for (const oldChild of oldChilds) {
      if (oldChild.key) {
        const nextChild = next.children.value.find(it => it.key === oldChild.key);
        if (!nextChild) {
          old.children.value = old.children.value.filter(it => it.key !== oldChild.key);
        }
      }
    }
  }

  old.render = next.render
  old.key = next.key;
  old.attributes = next.attributes;
  old.component = next.component;
  old.needs = next.needs;

  const z = zip(old.children.value, next.children.value)
  for (const [a, b] of z) {
    patchLayout(a, b)
  }

  next.children.value.slice(z.length).forEach((child) => {
    old.children.value.push(child)
  })

  return old
}


export const getLayoutByFieldPath = <State extends Dict, ComputedData extends Dict = Dict, ExternalData extends Dict = Dict>(path: string, layout: FormLayoutNode<State, ComputedData, ExternalData>): FormLayoutNode<State, ComputedData, ExternalData> | null => {
  const traverse = (layout: FormLayoutNode<State, ComputedData, ExternalData>): FormLayoutNode<State, ComputedData, ExternalData> | null => {
    if (layout.render === path && !unref(layout.hidden)) return layout;

    for (const child of layout.children.value) {
      const it = traverse(child);
      if (it) return it;
    }

    return null;
  }

  return traverse(layout);
}
